import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import checkoutService from '../../services/checkoutService';

const HEARTBEAT_INTERVAL_MS = 5_000;

/**
 * Keeps the launched kiosk checkout marked ONLINE for the lifetime of its
 * full-screen flow. The checkout ID and tenant ID are supplied by the launch
 * URL, so no tenant-context selection is required in the kiosk tab.
 */
export default function CheckoutStatusHeartbeat() {
  const location = useLocation();
  const [params] = useSearchParams();
  const orgId = params.get('orgId');
  const checkoutId = params.get('checkoutId');
  const isKioskFlow = /^\/checkout\/(camera|need-camera|pick-camera|pair|success)/.test(location.pathname);

  useEffect(() => {
    if (!isKioskFlow || !orgId || !checkoutId) return undefined;

    let active = true;
    const sendStatus = (status) =>
      checkoutService.updateCheckout(orgId, checkoutId, { status }).catch((error) => {
        // Heartbeats are best-effort; avoid disrupting the active kiosk flow.
        if (active) console.warn(`Unable to mark checkout ${checkoutId} ${status}`, error);
      });

    sendStatus('ONLINE');
    const intervalId = window.setInterval(() => sendStatus('ONLINE'), HEARTBEAT_INTERVAL_MS);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      // Use the same PATCH endpoint during route exit / tab close. The browser
      // may cancel ordinary asynchronous requests while closing, so keepalive
      // makes the final OFFLINE update eligible to complete in that case.
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL !== undefined
        ? import.meta.env.VITE_API_BASE_URL
        : 'http://localhost:8081';
      fetch(`${apiBaseUrl}/api/orgs/${orgId}/checkouts/${checkoutId}`, {
        method: 'PATCH',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'OFFLINE' }),
      }).catch(() => {
        // A closed tab cannot recover from a failed final status update.
      });
    };
  }, [isKioskFlow, orgId, checkoutId]);

  return null;
}
